import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import { Plus } from "@phosphor-icons/react";
import { SectionItem, SectionKey, SectionWithItem } from "@reactive-resume/schema";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import get from "lodash.get";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { CoachSectionBadge } from "../../../../_components/coach/coach-section-badge";
import {
  CoachSectionCollapseToggle,
  useCoachSectionBody,
} from "../../../../_components/coach/coach-section-collapse";
import { CoachSectionSuggestions } from "../../../../_components/coach/coach-section-suggestions";
import { getSectionIcon } from "./section-icon";
import { SectionListItem } from "./section-list-item";
import { SectionOptions } from "./section-options";

type Props<T extends SectionItem> = {
  id: SectionKey;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
  summary?: (item: T) => string | undefined;
};

const getDisplaySectionName = (id: SectionKey, name: string, template?: string) => {
  if (id === "volunteer") return "Social Impact";
  if (id === "awards" && template === "mastersunion") return "Achievements";

  return name;
};

export const SectionBase = <T extends SectionItem>({
  id,
  title,
  description,
  summary,
}: Props<T>) => {
  const { open } = useDialog(id);

  const setValue = useResumeStore((state) => state.setValue);
  const template = useResumeStore((state) => state.resume.data.metadata.template);
  const section = useResumeStore((state) =>
    get(state.resume.data.sections, id),
  ) as SectionWithItem<T>;

  const { visible: bodyVisible, collapsible: inCoachSession } = useCoachSectionBody(id);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!section) return null;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  const onCreate = () => open("create", { id });
  const onUpdate = (item: T) => open("update", { id, item });
  const onDuplicate = (item: T) => open("duplicate", { id, item });
  const onDelete = (item: T) => open("delete", { id, item });

  const onToggleVisibility = (index: number) => {
    const visible = get(section, `items[${index}].visible`, true);
    setValue(`sections.${id}.items[${index}].visible`, !visible);
  };

  return (
    <motion.section
      id={id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="grid gap-y-6"
    >
      <header className="flex items-center justify-between gap-x-3">
        <div className="flex min-w-0 items-center gap-x-4">
          {/* `text-3xl` sits in a 2.25rem line box around a 1.875rem glyph, so `items-center` centres
              the icon and the badge against the BOX — which puts them visibly above the text's own
              optical centre, since the slack is distributed around the ascender and descender rather
              than around the ink. `leading-none` collapses the box onto the glyphs so the flex
              centring lands where the eye expects. Coach-only: the base builder's header is
              deliberately left as it is. */}
          <span className="flex shrink-0 items-center">{getSectionIcon(id)}</span>

          <h2 className={cn("line-clamp-1 text-3xl font-bold", inCoachSession && "leading-none")}>
            {getDisplaySectionName(id, section.name, template)}
          </h2>
        </div>

        <div className="flex items-center gap-x-2">
          <CoachSectionBadge sectionId={id} />
          {/* Both render null outside a coach session, so the header is unchanged in the normal
              builder. */}
          <CoachSectionCollapseToggle sectionId={id} />
          <SectionOptions id={id} />
        </div>
      </header>

      {/* Collapsed by default DURING A COACH OPTIMIZATION ONLY — `bodyVisible` is always true in the
          normal builder, so nothing here is ever hidden outside a coach session.
          The suggestions are INSIDE the gate: a collapsed section shows only its header and its
          "AI Suggested (n)" count, and expanding it reveals the cards together with the content they
          rewrite. Keeping them out here meant a collapsed sidebar was still a stack of suggestion
          cards, which defeated collapsing it. */}
      {bodyVisible && (
        <>
          {/* The AI suggestions that target THIS section, shown next to the items they rewrite
              rather than in one undifferentiated list at the top of the sidebar. */}
          <CoachSectionSuggestions sectionId={id} />
          <main className={cn("grid transition-opacity", !section.visible && "opacity-50")}>
            {section.items.length === 0 && (
              <Button
                variant="outline"
                onClick={onCreate}
                className="gap-x-2 border-dashed py-6 leading-relaxed hover:bg-secondary-accent"
              >
                <Plus size={14} />
                <span className="font-medium">
                  {t({
                    message: "Add a new item",
                    context: "For example, add a new work experience, or add a new profile.",
                  })}
                </span>
              </Button>
            )}

            <DndContext
              sensors={sensors}
              onDragEnd={onDragEnd}
              collisionDetection={closestCenter}
              modifiers={[restrictToParentElement]}
            >
              <SortableContext items={section.items} strategy={verticalListSortingStrategy}>
                <AnimatePresence>
                  {section.items.map((item, index) => (
                    <SectionListItem
                      id={item.id}
                      key={item.id}
                      isMastersUnion={template === "mastersunion"}
                      visible={item.visible}
                      title={title(item as T)}
                      description={description?.(item as T)}
                      summary={summary?.(item as T)}
                      onUpdate={() => onUpdate(item as T)}
                      onDelete={() => onDelete(item as T)}
                      onDuplicate={() => onDuplicate(item as T)}
                      onToggleVisibility={() => onToggleVisibility(index)}
                    />
                  ))}
                </AnimatePresence>
              </SortableContext>
            </DndContext>
          </main>

          {section.items.length > 0 && (
            <footer className="flex items-center justify-end">
              <Button variant="outline" className="ml-auto gap-x-2" onClick={onCreate}>
                <Plus />
                <span>
                  {t({
                    message: "Add a new item",
                    context: "For example, add a new work experience, or add a new profile.",
                  })}
                </span>
              </Button>
            </footer>
          )}
        </>
      )}
    </motion.section>
  );
};
